import React, { Suspense, useState } from 'react';
import { Box, CircularProgress, IconButton, Paper, Fab, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  CropSquare as MaximizeIcon,
  Minimize as MinimizeIcon,
  CloseFullscreen as CloseFullscreenIcon,
} from '@mui/icons-material';
import { useDraggable } from '../../../hooks/useDraggable';
import lazyWithRetry from '../../../utils/lazyWithRetry';
import assistantAvatar from '../../../assets/chatbot_admin.png';

const AIKnowledgePage = lazyWithRetry(() => import('../pages/AIKnowledgePage'));

export const FloatingAdminAssistant = () => {
  const [mode, setMode] = useState<'hidden' | 'compact' | 'maximized'>('hidden');
  // Position is offset from bottom-right
  const { position, onPointerDown, onPointerMove, onPointerUp, wasDragged, resetPosition } = useDraggable({ x: 0, y: 0 });

  const isMaximized = mode === 'maximized';

  if (mode === 'hidden') {
    return (
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => {
          onPointerUp(e);
          // If it wasn't dragged, treat it as a click
          if (!wasDragged.current) {
            resetPosition();
            setMode('compact');
          }
        }}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1500,
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <Tooltip title="Admin Assistant" placement="left">
          <Fab
            className="allow-drag"
            aria-label="admin-assistant"
            color="info"
            sx={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
          >
            <img src={assistantAvatar} alt="Admin Assistant" draggable={false} style={{ width: 32, height: 32, borderRadius: '50%', userSelect: 'none', pointerEvents: 'none' }} />
          </Fab>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      style={{
        transform: isMaximized ? 'none' : `translate(${position.x}px, ${position.y}px)`
      }}
      sx={{
        position: 'fixed',
        ...(isMaximized
          ? {
              top: '5vh',
              left: '5vw',
              width: '90vw',
              height: '90vh',
            }
          : {
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              left: { xs: 16, sm: 'auto' },
              width: { xs: 'auto', sm: 360, md: 380 },
              height: { xs: 'calc(100vh - 80px)', sm: 'min(600px, calc(100vh - 120px))' },
              maxHeight: 'calc(100vh - 32px)',
            }),
        zIndex: 1500,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMaximized ? 3 : 3,
        overflow: 'hidden',
        transition: isMaximized ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header / Drag Handle */}
      <Box
        onPointerDown={isMaximized ? undefined : onPointerDown}
        onPointerMove={isMaximized ? undefined : onPointerMove}
        onPointerUp={isMaximized ? undefined : onPointerUp}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: isMaximized ? 'default' : 'grab',
          '&:active': {
            cursor: isMaximized ? 'default' : 'grabbing',
          }
        }}
      >
        <img src={assistantAvatar} alt="Assistant" draggable={false} style={{ width: 24, height: 24, marginLeft: 8, marginRight: 8, borderRadius: '50%', userSelect: 'none', pointerEvents: 'none' }} />
        <Box sx={{ flex: 1, fontWeight: 600, fontSize: 15, userSelect: 'none' }}>
          Admin Assistant
        </Box>
        
        {/* Controls */}
        <IconButton size="small" onClick={() => { resetPosition(); setMode('hidden'); }} sx={{ mr: 0.5 }}>
          <MinimizeIcon fontSize="small" />
        </IconButton>
        
        {isMaximized ? (
          <IconButton size="small" onClick={() => { resetPosition(); setMode('compact'); }} sx={{ mr: 0.5 }}>
            <CloseFullscreenIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={() => { resetPosition(); setMode('maximized'); }} sx={{ mr: 0.5 }}>
            <MaximizeIcon fontSize="small" />
          </IconButton>
        )}
        
        <IconButton size="small" onClick={() => { resetPosition(); setMode('hidden'); }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content — load jsPDF/html2canvas/docx/markdown only when the panel opens */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Suspense
          fallback={
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          }
        >
          <AIKnowledgePage isWidget={true} isCompact={!isMaximized} />
        </Suspense>
      </Box>
    </Paper>
  );
};
